import { Copy, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { StatusBadge } from "@/components/common";
import { act } from "@/lib/hooks";
import { api, fmt, fdatetime } from "@/lib/api";

const OPEN = ["pending", "failed", "overdue"];

export const chargedGhs = (i) => i.charge_amount ?? ((i.currency || "GHS") === "GHS" ? i.amount : null);

export function InvoiceDetail({ inv, tenantId, onClose }) {
  if (!inv) return null;
  const ghs = chargedGhs(inv);
  const copy = () => navigator.clipboard.writeText(inv.authorization_url).then(() => toast.success("Payment link copied"), () => toast.error("Copy failed"));
  const resend = async () => {
    const r = await act(() => api.post(`/platform/tenants/${tenantId}/invoices/${inv.id}/resend`));
    if (r) (r.status === "sent" ? toast.success(`Link sent to ${r.to}`) : toast.warning(`Not sent (${r.status.replace(/_/g, " ")})`));
  };
  const remind = async () => {
    const r = await act(() => api.post(`/platform/tenants/${tenantId}/invoices/${inv.id}/remind`));
    if (r) (r.status === "sent" ? toast.success(`Reminder sent to ${r.to}`) : toast.warning(`Reminder not sent (${r.status.replace(/_/g, " ")})`));
  };
  const rows = [["Number", inv.number], ["Date", fdatetime(inv.created_at)], ["Description", (inv.lines || []).map((l) => l.description).join(", ") || "—"],
    ["Type", (inv.kind || "").replace(/_/g, " ")], ["Status", <StatusBadge key="s" status={inv.status} />], ["Amount (USD)", fmt(inv.amount, inv.currency || "USD")],
    ["Charged (GHS)", ghs != null ? fmt(ghs, "GHS") : "Not charged via Paystack"], ["Exchange rate", inv.fx_rate ? `1 USD = ${inv.fx_rate} GHS` : "—"],
    ["Paystack reference", <span key="r" className="font-mono text-xs">{inv.reference || "—"}</span>]];
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="invoice-detail">
        <SheetHeader className="text-left"><SheetTitle>Invoice {inv.number}</SheetTitle><SheetDescription>{inv.tenant_name}</SheetDescription></SheetHeader>
        <dl className="mt-5 space-y-3 text-sm">{rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-2"><dt className="text-slate-500">{k}</dt><dd className="text-right font-medium" data-testid={`invoice-detail-${k.split(" ")[0].toLowerCase()}`}>{v}</dd></div>))}</dl>
        {OPEN.includes(inv.status) && <div className="mt-6 space-y-2" data-testid="invoice-detail-payment">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Paystack payment link</p>
          {inv.authorization_url ? <>
            <div className="flex gap-2"><Input readOnly value={inv.authorization_url} data-testid="invoice-detail-link" /><Button variant="outline" data-testid="invoice-detail-copy" onClick={copy}><Copy className="h-4 w-4" /></Button></div>
            <Button data-testid="invoice-detail-resend" onClick={resend} className="w-full bg-[#13294B] hover:bg-[#1c3a66]"><Send className="mr-1 h-4 w-4" />Resend to billing email</Button>
          </> : <p className="text-sm text-slate-500" data-testid="invoice-detail-no-link">No Paystack link yet. Use Issue invoice, or the tenant can press Pay on their billing page.</p>}
          {inv.status === "pending" && <Button variant="outline" data-testid="invoice-detail-remind" onClick={remind} className="w-full"><Send className="mr-1 h-4 w-4" />Send reminder now</Button>}
          {inv.reminder_sent_at && <p className="text-xs text-slate-400" data-testid="invoice-detail-reminder-info">Last reminder {fdatetime(inv.reminder_sent_at)} ({(inv.reminder_status || "").replace(/_/g, " ")})</p>}
        </div>}
      </SheetContent>
    </Sheet>
  );
}
